// Uploaded background-music store. Audio files are far too large for localStorage,
// so the blobs live in IndexedDB (db "revault-music", store "tracks"); a lightweight
// index of { id, name, type } is mirrored in localStorage under MUSIC_KEY via the
// generic store, so the player + settings can list tracks synchronously and stay in
// sync across tabs. The audio never leaves this browser.
import { MUSIC_KEY } from "./settings-schema.js";
import { readJSON, writeJSON, subscribe } from "./store.js";

const DB_NAME = "revault-music";
const STORE = "tracks";
const MAX_TRACKS = 40;
const MAX_BYTES = 30 * 1024 * 1024; // 30 MB per file - a generous song, keeps the DB sane

let dbPromise = null;
let mutations = Promise.resolve();
function serialize(fn) {
	const result = mutations.then(fn);
	mutations = result.catch(() => {});
	return result;
}
function openDB() {
	if (dbPromise) return dbPromise;
	const opening = new Promise((resolve, reject) => {
		let req, failed = false;
		const fail = () => { failed = true; reject(req?.error || new Error("Music storage unavailable")); };
		try { req = indexedDB.open(DB_NAME, 1); } catch (e) { reject(e); return; }
		req.onupgradeneeded = () => { const db = req.result; if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: "id" }); };
		req.onsuccess = () => {
			const db = req.result;
			if (failed) { db.close(); return; }
			db.onversionchange = () => { db.close(); if (dbPromise === opening) dbPromise = null; };
			db.onclose = () => { if (dbPromise === opening) dbPromise = null; };
			resolve(db);
		};
		req.onerror = req.onblocked = fail;
	});
	dbPromise = opening;
	void opening.catch(() => { if (dbPromise === opening) dbPromise = null; });
	return opening;
}
async function request(mode, operation) {
	const db = await openDB();
	return new Promise((resolve, reject) => {
		const transaction = db.transaction(STORE, mode);
		const result = operation(transaction.objectStore(STORE));
		transaction.oncomplete = () => resolve(result.result);
		transaction.onabort = transaction.onerror = () => reject(transaction.error || new Error("Music storage failed"));
	});
}

// The synchronous index (names only) - what the UI renders from.
export function listTracks() {
	const v = readJSON(MUSIC_KEY, []);
	return Array.isArray(v) ? v : [];
}
export function subscribeTracks(fn) { return subscribe(MUSIC_KEY, fn); }

function newId() {
	try { if (crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
	return "t" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

// A committed write must finish before its track appears in the playlist.
export function addTracks(files) {
	const list = Array.from(files || []);
	return serialize(async () => {
		let index = listTracks();
		let added = 0, skipped = 0, failed = 0;
		for (const f of list) {
			if (index.length >= MAX_TRACKS) { skipped++; continue; }
			if (!/^audio\//.test(f.type || "") && !/\.(mp3|m4a|aac|ogg|oga|wav|flac|opus|weba)$/i.test(f.name || "")) { skipped++; continue; }
			if (f.size > MAX_BYTES) { skipped++; continue; }
			const id = newId();
			const rec = { id, name: (f.name || "Track").replace(/\.[^.]+$/, "").slice(0, 80), type: f.type || "audio/mpeg", blob: f, addedAt: Date.now() };
			try {
				await request("readwrite", (store) => store.put(rec));
				index = index.concat([{ id, name: rec.name, type: rec.type }]);
				added++;
			} catch (e) { failed++; }
		}
		if (added) writeJSON(MUSIC_KEY, index); // broadcasts to subscribers + other tabs
		return { added, skipped, failed };
	});
}

export async function getTrackBlob(id) {
	try { const rec = await request("readonly", (store) => store.get(id)); return rec ? rec.blob : null; }
	catch (e) { return null; }
}

export function removeTrack(id) {
	return serialize(async () => {
		try { await request("readwrite", (store) => store.delete(id)); } catch (e) { return false; }
		writeJSON(MUSIC_KEY, listTracks().filter((t) => t.id !== id));
		return true;
	});
}

export function clearTracks() {
	return serialize(async () => {
		try { await request("readwrite", (store) => store.clear()); } catch (e) { return false; }
		writeJSON(MUSIC_KEY, []);
		return true;
	});
}
